import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ElementType,
  type MouseEvent,
  forwardRef,
} from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';

type PathType = string | {pathname: string; search?: string; hash?: string};

const Tabs = TabsPrimitive.Root;

export const tabsListVariants = cva('inline-flex items-center', {
  variants: {
    variant: {
      button:
        'tab-list h-[42px] justify-center gap-[7px] rounded-md border border-[var(--tabs-list-border)] bg-[var(--tabs-list-bg)] p-[3px] [&>*:not(:last-child)]:after:content-[""] [&>*:not(:last-child)]:after:absolute [&>*:not(:last-child)]:after:right-[-4px] [&>*:not(:last-child)]:after:h-6 [&>*:not(:last-child)]:after:w-px [&>*:not(:last-child)]:after:bg-[var(--tabs-list-separator)]',
      underlined:
        'tab-list mb-8 flex-wrap gap-x-6 gap-y-3 border-b border-[var(--tabs-list-border)]',
    },
  },
  defaultVariants: {
    variant: 'button',
  },
});

export type TabsListProps = ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>;

const TabsList = forwardRef<ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({className, variant, ...props}, ref) => (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({variant}), className)}
      {...props}
    />
  ),
);
TabsList.displayName = TabsPrimitive.List.displayName;

export const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 cursor-pointer select-none',
  {
    variants: {
      variant: {
        button:
          'h-full flex-1 rounded-md px-4 text-center text-[15px] font-medium relative text-[var(--tabs-trigger-text)] hover:bg-[var(--tabs-trigger-hover-bg)] hover:text-[var(--tabs-trigger-hover-text)] data-[state=active]:bg-[var(--tabs-trigger-active-bg)] data-[state=active]:font-bold data-[state=active]:text-[var(--tabs-trigger-active-text)] data-[state=active]:shadow-sm [&.active]:bg-[var(--tabs-trigger-active-bg)] [&.active]:font-bold [&.active]:text-[var(--tabs-trigger-active-text)] [&.active]:shadow-sm',
        underlined:
          'h-full grow relative top-px pb-[15px] text-base font-medium leading-5 text-[var(--tabs-trigger-text)] border-b-2 border-transparent -mb-px data-[state=active]:text-[var(--tabs-trigger-active-border)] data-[state=active]:border-[var(--tabs-trigger-active-border)] hover:border-[color-mix(in_srgb,var(--tabs-trigger-active-border)_30%,transparent)] [&.active]:text-[var(--tabs-trigger-active-border)] [&.active]:border-[var(--tabs-trigger-active-border)] px-3',
      },
    },
    defaultVariants: {
      variant: 'button',
    },
  },
);

type BaseTabsTriggerProps = Omit<
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
  'value' | 'onClick'
> &
  VariantProps<typeof tabsTriggerVariants> & {
    value: string;
    hidden?: boolean;
    end?: boolean;
  };

type LinkTabsTriggerProps = Omit<BaseTabsTriggerProps, 'value'> & {
  value: PathType;
  end?: boolean;
  asLink: ElementType;
  onClick?: (event: MouseEvent<HTMLElement>, pathName?: PathType) => void;
};

type ButtonTabsTriggerProps = BaseTabsTriggerProps & {
  asLink?: undefined;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export type TabsTriggerProps = ButtonTabsTriggerProps | LinkTabsTriggerProps;

const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({className, variant = 'button', hidden, ...props}, ref) => {
  if (hidden) {
    return null;
  }

  if (props.asLink) {
    const {asLink: Link, value, end, onClick, children, ...linkProps} = props;

    return (
      <Link
        relative="route"
        end={end}
        to={value}
        onClick={(event: MouseEvent<HTMLElement>) => onClick?.(event, value)}
        className={cn(
          tabsTriggerVariants({variant}),
          variant === 'button'
            ? 'tabs__btn-item tabs__btn-item_link'
            : 'tabs__item tabs__item_link',
          className,
        )}
        {...linkProps}
      >
        {children}
      </Link>
    );
  }

  const {children, onClick, end: _end, ...triggerProps} = props;

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        tabsTriggerVariants({variant}),
        variant === 'button' ? 'tabs__btn-item' : 'tabs__item',
        className,
      )}
      onClick={onClick}
      {...triggerProps}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({className, ...props}, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('tabs__header-item', className)}
    tabIndex={-1}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {Tabs, TabsList, TabsTrigger, TabsContent};
